import { createRouter, createWebHistory } from '@ionic/vue-router';
import { handleRouteChangeForSessionReplay } from '@/utils/datadog';
import i18n from '../utils/i18n';
import { Browser } from '@capacitor/browser';
import Team from '@/pages/team.vue';
import More from '@/pages/more/index.vue';
import Login from '@/pages/login/index.vue';
import Home from '../pages/home/index.vue';
import Experience from '../pages/experience/index.vue';
import Schedule from '../pages/schedule/index.vue';
import SessionCard from '../pages/schedule/session-card/_name.vue';
import Announcement from '../pages/announcement/index.vue';
import Resources from '../pages/resources/index.vue';
import ResourceSearch from '../pages/resources/resource-search.vue';
import ResourceBookmarks from '../pages/resources/bookmark.vue';
import ResourcesSessions from '../pages/resources/sessions.vue';
import ResourcesBooks from '../pages/resources/book.vue';
import ResourcesGensMvmt from '../pages/resources/gensmvmt.vue';
import PhotoDrop from '../pages/photodrop.vue';
import PhotodropWelcome from '../pages/photodrop/welcome.vue';
import Survey from '../pages/survey/_name.vue';
import AccessDenied from '../pages/login/access-denied.vue';
import TermsOfUse from '../pages/terms-of-use.vue';
import PrivacyPolicy from '../pages/privacy-policy.vue';
import Conference from '../pages/conference.vue';
import { createAuthGuard } from '@auth0/auth0-vue';
import ExpSummary from '../pages/experience/summary.vue';
import { isNativeNotWeb, isIosNativeOnly, isAndroidNativeOnly } from '@/utils/platform-check';
import Profile from '../pages/profile/index.vue';
import PubProfile from '../pages/profile/pub/_id.vue';
import EditProfile from '../pages/profile/edit.vue';
import QRCode from '../pages/qr-code.vue';
import ProfileCrop from '../pages/profile/crop.vue';
import PreSignIn from '../pages/pre-sign-in.vue';
import ResourceFilter from '../pages/resources/filter-results.vue';
import VideoPage from '../pages/video/_video.vue';
import StudentGuidePage from '../pages/download/student-guide.vue';
import { auth0 } from '@/utils/auth0';
import store from '@/store';
import GPCR from '@/pages/gpcr/home/index.vue';
import GPCRAnnouncement from '@/pages/gpcr/announcement/index.vue';
import GPCRSchedule from '@/pages/gpcr/schedule/index.vue';
import GPCRConference from '../pages/gpcr/conference.vue';
import GPCRLanguage from '../pages/gpcr/language.vue';
import GPCRExperience from '../pages/gpcr/experience/index.vue';
import GPCRPhotodrop from '../pages/gpcr/photodrop.vue';
import GPCRResources from '../pages/gpcr/resources/index.vue';
import GPCRResourceSearch from '../pages/gpcr/resources/resource-search.vue';
import GPCRResourceBookmarks from '../pages/gpcr/resources/bookmark.vue';
import GPCRResourcesSessions from '../pages/gpcr/resources/sessions.vue';
import GPCRResourcesBooks from '../pages/gpcr/resources/book.vue';
import GPCRResourceFilter from '../pages/gpcr/resources/filter-results.vue';
import GPCRTeam from '@/pages/gpcr/team.vue';
import GPCRSessionCard from '../pages/gpcr/schedule/session-card/_name.vue';
import GPCRVideoPage from '../pages/gpcr/video/_video.vue';
import UrlPage from '../pages/url.vue';
import Callback from '@/pages/callback/index.vue';
import GICHome from '../pages/gic/home/index.vue';
import GICResources from '../pages/gic/resources/index.vue';
import GICResourcesSessions from '../pages/gic/resources/sessions.vue';
import GICResourceSearch from '../pages/gic/resources/resource-search.vue';
import GICResourceBookmarks from '../pages/gic/resources/bookmark.vue';
import GICAnnouncement from '../pages/gic/announcement/index.vue';
import GICPhotodrop from '../pages/gic/photodrop.vue';
import GICPhotodropGic3 from '../pages/gic/photodrop-gic3.vue';
import GICConference from '../pages/gic/conference.vue';
import GICSchedule from '../pages/gic/schedule/index.vue';
import GICVideoPage from '../pages/gic/video/_video.vue';
import { createLogoutUri, storeLoginPath } from '@/utils/redirect-uri';
import { getCurrentConferenceType } from '../utils/conference-type';
import {
  isExcludedPath,
  buildRouteVariantMap,
  resolveConferenceRoute,
} from './conference-resolution';
import { StatusBar, Style } from '@capacitor/status-bar';

async function handleLogoutOnError(err) {
  if (err.error === 'login_required') {
    await auth0.logout({
      logoutParams: {
        returnTo: createLogoutUri(process.env.VUE_APP_PACKAGE_ID, process.env.VUE_APP_AUTH0_DOMAIN),
      },
      openUrl: async url =>
        await Browser.open({
          url,
          windowName: '_self',
        }),
    });
  }
  console.error('Access token error:', err.error, err);
}

const routes = [
  {
    path: '/',
    redirect: isNativeNotWeb() ? '/pre-sign-in' : '/login',
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/login',
    component: Login,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/home',
    component: Home,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/experience',
    component: Experience,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/experience/summary',
    component: ExpSummary,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/schedule',
    component: Schedule,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/schedule/session-card/:name',
    component: SessionCard,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/announcement',
    component: Announcement,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/resources',
    component: Resources,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/resources/sessions',
    component: ResourcesSessions,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/resources/sessions/search',
    component: ResourceSearch,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/resources/sessions/mylist',
    component: ResourceBookmarks,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/resources/sessions/filter',
    component: ResourceFilter,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/resources/book',
    component: ResourcesBooks,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/resources/more-resources',
    component: ResourcesGensMvmt,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/team',
    component: Team,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/more',
    component: More,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/photodrop/welcome',
    component: PhotodropWelcome,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/photodrop',
    component: PhotoDrop,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/survey/:name',
    // Same Paperform shell as /survey/:name — optional URL shape for CMS/deep links
    alias: '/forms/:name',
    component: Survey,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/terms-of-use',
    component: TermsOfUse,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/privacy-policy',
    component: PrivacyPolicy,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/conference',
    component: Conference,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/settings',
    redirect: '/profile',
  },
  {
    path: '/access-denied',
    component: AccessDenied,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/profile',
    component: Profile,
    meta: { statusBarStyle: 'LIGHT' },
  },
  {
    path: '/resources/video/:video',
    component: VideoPage,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/profile/pub/:id',
    component: PubProfile,
    meta: { statusBarStyle: 'LIGHT' },
  },
  {
    path: '/profile/edit',
    component: EditProfile,
    meta: { statusBarStyle: 'LIGHT' },
  },
  {
    path: '/qr-code',
    component: QRCode,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/profile/crop',
    component: ProfileCrop,
    meta: { statusBarStyle: 'LIGHT' },
  },
  {
    path: '/download/student-guide',
    component: StudentGuidePage,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/gpcr/home',
    component: GPCR,
    meta: { statusBarStyle: 'LIGHT' },
  },
  {
    path: '/gpcr/announcement',
    component: GPCRAnnouncement,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/gpcr/schedule',
    component: GPCRSchedule,
    meta: { statusBarStyle: 'LIGHT' },
  },
  {
    path: '/gpcr/conference',
    component: GPCRConference,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/gpcr/language',
    component: GPCRLanguage,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/gpcr/experience',
    component: GPCRExperience,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/gpcr/photodrop',
    component: GPCRPhotodrop,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/gpcr/resources',
    component: GPCRResources,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/gpcr/resources/sessions',
    component: GPCRResourcesSessions,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/gpcr/resources/sessions/search',
    component: GPCRResourceSearch,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/gpcr/resources/sessions/mylist',
    component: GPCRResourceBookmarks,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/gpcr/resources/sessions/filter',
    component: GPCRResourceFilter,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/gpcr/resources/book',
    component: GPCRResourcesBooks,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/gpcr/team',
    component: GPCRTeam,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/gpcr/schedule/session-card/:name',
    component: GPCRSessionCard,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/gpcr/resources/video/:video',
    component: GPCRVideoPage,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/url',
    component: UrlPage,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/gic/home',
    component: GICHome,
    meta: { statusBarStyle: 'LIGHT' },
  },
  {
    path: '/gic/resources',
    component: GICResources,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/gic/resources/sessions',
    component: GICResourcesSessions,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/gic/resources/sessions/mylist',
    component: GICResourceBookmarks,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/gic/resources/sessions/search',
    component: GICResourceSearch,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/gic/photodrop',
    component: GICPhotodrop,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/gic/photodrop-gic3',
    component: GICPhotodropGic3,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/gic/announcement',
    component: GICAnnouncement,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/gic/schedule',
    component: GICSchedule,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/gic/conference',
    component: GICConference,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/gic/resources/video/:video',
    component: GICVideoPage,
    meta: { statusBarStyle: 'DARK' },
  },
  {
    path: '/callback',
    component: Callback,
    meta: { statusBarStyle: 'DARK' },
  },
];

if (isNativeNotWeb()) {
  routes.push({
    path: '/pre-sign-in',
    component: PreSignIn,
    meta: { statusBarStyle: 'DARK' },
  });
}

// Apply auth guard for web (non-native) routes
const isWebProduction = process.env.VUE_APP_NODE_ENV !== 'local' && !isNativeNotWeb();
if (isWebProduction) {
  routes.forEach(route => {
    if (!isExcludedPath(route.path)) {
      route.beforeEnter = createAuthGuard({
        redirectLoginOptions: {
          appState: { returnTo: window.location.pathname },
        },
      });
    }
  });
}

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

// Built once after routes are defined — maps base paths to available conference prefix variants
const routeVariantMap = buildRouteVariantMap(routes);

// Local-mode (VUE_APP_NODE_ENV=local) navigation: a localStorage token stands
// in for Auth0, then profile fetch + conference-variant resolution run exactly
// as in production so local dev and E2E exercise the same mode-drives-route
// behaviour (previously local mode returned early and variant redirects never
// ran). Returns a router redirect object, or undefined to proceed.
async function resolveLocalModeNavigation(to) {
  const localToken = localStorage.getItem('token');
  if (!localToken && to.path !== '/login') {
    return { path: '/login' };
  }
  if (!localToken || to.path === '/login' || isExcludedPath(to.path)) {
    return undefined;
  }
  store.commit('user/setToken', localToken);
  try {
    await store.dispatch('profile/fetchProfile');
    const { confYear, conferences } = store.state.profile;
    const confType = getCurrentConferenceType(confYear, conferences);
    const redirectPath = resolveConferenceRoute(
      confType,
      conferences,
      to.fullPath,
      routeVariantMap,
    );
    if (redirectPath) {
      return { path: redirectPath, replace: true };
    }
  } catch (err) {
    // Variant resolution must never lock a local dev out of the app.
    console.error('[Router] local-mode variant resolution failed:', err);
  }
  return undefined;
}

router.beforeEach(async (to, from) => {
  // Handle access denied error from query params
  if (to.query.error === 'access_denied' && to.query.error_description === 'access denied') {
    return { path: '/access-denied', replace: true };
  }

  // Local development: simple token check instead of Auth0, but with the same
  // mode-drives-route conference resolution as production.
  if (process.env.VUE_APP_NODE_ENV === 'local') {
    return resolveLocalModeNavigation(to);
  }

  // Store intended destination for web deep links (before auth guard redirects to login)
  // Exclude /callback so Auth0's return redirect doesn't overwrite the stored path
  if (from.path === '/' && to.path !== '/login' && to.path !== '/callback') {
    storeLoginPath(window.location.href);
  }

  // Native app auth check for protected routes
  const needsAuth = !isExcludedPath(to.path) && to.path !== '/login' && isNativeNotWeb();
  if (needsAuth && !auth0.isAuthenticated.value) {
    return { path: '/login' };
  }

  try {
    const token = await auth0.getAccessTokenSilently({ cacheMode: 'cache-only' });
    store.commit('user/setToken', token);

    await store.dispatch('profile/fetchProfile');
    const { confYear, conferences } = store.state.profile;
    const confType = getCurrentConferenceType(confYear, conferences);
    console.log('[Router] conferences from profile:', conferences);
    console.log('[Router] resolved confType:', confType);

    // Force English locale for Global and GIC routes
    if (confType === 'Global' || confType === 'GIC') {
      i18n.locale = 'en';
    }

    // Mode drives route — resolve to correct conference prefix
    const redirectPath = resolveConferenceRoute(
      confType,
      conferences,
      to.fullPath,
      routeVariantMap,
    );
    if (redirectPath) {
      console.log('[Router] redirecting to:', redirectPath);
      return { path: redirectPath, replace: true };
    }
    console.log('[Router] no redirect, staying on:', to.fullPath);
  } catch (err) {
    await handleLogoutOnError(err);
  }
});

router.afterEach(async to => {
  const isDark = to.meta.statusBarStyle === 'DARK';

  try {
    if (isIosNativeOnly()) {
      // iOS: setStyle changes the icon/text color
      if (isDark) {
        await StatusBar.setStyle({ style: Style.Light });
      } else {
        await StatusBar.setStyle({ style: Style.Dark });
      }
    } else if (isAndroidNativeOnly()) {
      // Android: always black background with white icons (matching navigation bar)
      await StatusBar.setBackgroundColor({ color: '#000000' });
      await StatusBar.setStyle({ style: Style.Dark });
    }
  } catch (e) {
    console.warn('StatusBar not available');
  }

  handleRouteChangeForSessionReplay(to.path);
});

export default router;
